import { chunk, merge } from 'lodash-es';
import { computed, ref, unref, type Ref, type ComputedRef } from 'vue';
import { useRoute } from 'vue-router';
import { useStore } from 'vuex';
import { floorDiscountedPrice } from '@utils/pricing';

type MaybeRef<T> = T | Ref<T> | ComputedRef<T>;

function resolve<T>(value: MaybeRef<T>): T {
    return unref(value);
}

interface UsePurchasingDeps {
    productCurrency: MaybeRef<string | undefined>;
    purchasePageCTA?: MaybeRef<any>;
    settings?: MaybeRef<any>;
    products?: MaybeRef<any>;
    route?: MaybeRef<{ query?: Record<string, any>; path?: string }>;
}

export function usePurchasing(deps: UsePurchasingDeps) {
    const store = useStore();
    const promotion = ref<Record<string, any>>({});
    let routeFromRouter: ReturnType<typeof useRoute> | null = null;

    try {
        routeFromRouter = useRoute();
    } catch {
        routeFromRouter = null;
    }

    const route = computed(() => {
        if (deps.route) return resolve(deps.route);
        return routeFromRouter || { query: {}, path: '' };
    });

    const promotions = computed(() => store.state.promotions?.data || []);
    const licensesItems = computed(() => store.state.licenses?.data || []);
    const productItems = computed(() => store.state.products?.items || {});
    const internalProductId = computed(() => store.state.pages?.page?.product_id || null);

    function findPromotionForEmails(productID: number | string | null, coupon: string) {
        if (!productID) return {};

        const licenseIDs = licensesItems.value.map((item: any) => parseInt(item.cleverbridge_id, 10));

        return promotions.value
            .filter((item: any) => item.is_for_emails_only && item.product_id === productID && item.coupon_code === coupon)
            .find((item: any) => item.license_ids.find((id: number) => licenseIDs.includes(id))) || {};
    }

    const hasEmailPromo = computed(() => {
        const coupon = route.value.query?.coupon || '';
        if (!coupon) return false;

        if (!route.value.path?.includes('purchasing')) return false;

        const purchasePageCTA = deps.purchasePageCTA ? resolve(deps.purchasePageCTA) : undefined;
        const productID = purchasePageCTA?.productId || internalProductId.value;
        const promotionForEmails = findPromotionForEmails(productID, String(coupon));

        return (promotionForEmails as any).discount > 0;
    });

    const promotionList = computed(() => {
        const coupon = route.value.query?.coupon || '';
        const isForEmailsOnly = hasEmailPromo.value;

        return promotions.value.filter((item: any) => {
            if (!isForEmailsOnly) return !item.is_for_emails_only;
            return item.is_for_emails_only && item.coupon_code === coupon;
        });
    });

    function generateSessionURL(payload: any) {
        return store.dispatch('cleverbridge/get', payload);
    }

    function recursiveSearch(obj: any, searchKey: string, results: any[] = []) {
        Object.keys(obj || {}).forEach((key) => {
            if (key === searchKey) {
                results.push(obj);
                return;
            }
            if (typeof obj[key] === 'object' && obj[key] !== null) {
                recursiveSearch(obj[key], searchKey, results);
            }
        });

        return results;
    }

    function productCTA(settings: any) {
        const type = settings?.isPurchase ? 'purchasePageCTA' : 'productPageCTA';
        const productId = settings?.productId || internalProductId.value;
        return productItems.value?.[productId]?.parameters?.common?.[type] || {};
    }

    function promotionActive(productId: number | string | null, cleverBridgeCartIDs: number[]) {
        if (!productId) return {};
        if (!cleverBridgeCartIDs?.length) return {};

        return promotionList.value.find((item: any) => {
            if (item.product_id !== productId) return false;
            return cleverBridgeCartIDs.find((id) => item.license_ids?.includes(id));
        }) || {};
    }

    async function getLicenses(settings: any) {
        const locale = settings?.locale ||
            store.state.geolocation?.desiredLocale ||
            store.state.geolocation?.requestedLocale;

        await store.dispatch('locales/getLocaleData', { locale });

        const PAGINATE_SIZE = 50;
        const payload = {
            locale,
            currency: resolve(deps.productCurrency),
            paginate: { number: 1, size: PAGINATE_SIZE },
        };

        const pageSettingsCTA = productCTA(settings);

        const license = merge({}, pageSettingsCTA, settings);
        const licenses = license?.cleverBridgeCartList || [license?.cleverBridgeCartID] || [];

        if (licenses.length === 1) {
            const cleverBridgeCartID = licenses[0];

            // Keeping original parity:
            // original code used this.settings?.productId here, not the method arg.
            const componentSettings = deps.settings ? resolve(deps.settings) : undefined;
            const productId = componentSettings?.productId || internalProductId.value;

            const promoLocal = promotionActive(productId, [cleverBridgeCartID]);
            const promoLocalLicenseID = (promoLocal as any).promoSettings?.product_page_license_id;

            const promoGlobal = promotionList.value.find(
                (item: any) => item.product_id === productId && !item.is_for_emails_only,
            ) || {};
            const promoGlobalLicenseID = (promoGlobal as any).promoSettings?.product_page_license_id;

            licenses.push(promoLocalLicenseID || promoGlobalLicenseID);
        }

        const promisesWithOutPromo = chunk(licenses.filter(Number), PAGINATE_SIZE)
            .map((cleverbridgeIDs) => store.dispatch('licenses/getList', { ...payload, cleverbridgeIDs }));

        await Promise.all(promisesWithOutPromo);
    }

    function findCleverBridgeCart(slice: any, settings: any) {
        const licenses = recursiveSearch(slice || {}, 'cleverBridgeCartID').flat(2);
        const cleverBridgeCartList = licenses.map((license: any) => license.cleverBridgeCartID).flat(2);
        return getLicenses({ ...settings, cleverBridgeCartList });
    }

    function licenseData({ cleverBridgeCartID, settingsCTA }: { cleverBridgeCartID?: number | string; settingsCTA?: any }) {
        const resultDefault = { price: 0, price_promo: 0 };
        const cleverbBridgeID = (cleverBridgeCartID || settingsCTA?.cleverBridgeCartID || '').toString();
        if (!cleverbBridgeID) return resultDefault;

        const compare = (i: any) => i.cleverbridge_id === cleverbBridgeID && i.currency === resolve(deps.productCurrency);
        const license = licensesItems.value.find(compare) || resultDefault;
        const productId = (license as any).product_id;
        const promotionActiveItem = promotionActive(productId, [Number(cleverBridgeCartID)]);
        const promotionData = merge({}, promotionActiveItem, settingsCTA);
        const discounts = (promotionData as any).discounts || [];
        const promotionItem = discounts.find((item: any) => item.list.includes(cleverBridgeCartID)) || {};
        const discount = discounts.length
            ? (promotionItem.discount || 0)
            : ((promotionData as any).discount || 0);

        if (discount) {
            return {
                ...(license as any),
                ...promotionData,
                discount,
                cleverbridge_id: cleverBridgeCartID,
                price_promo: floorDiscountedPrice((license as any).price, discount),
            };
        }

        return license;
    }

    function setPromo() {
        const purchasePageCTA = deps.purchasePageCTA ? resolve(deps.purchasePageCTA) : undefined;
        const products = deps.products ? resolve(deps.products) : undefined;

        const productId = purchasePageCTA?.productId || internalProductId.value;
        const cleverBridgeCartIDs = licensesItems.value.map((item: any) => Number(item.cleverbridge_id));
        const promotionActiveItem = promotionActive(productId, cleverBridgeCartIDs);

        const nextPromotion = merge({}, promotionActiveItem, purchasePageCTA);
        nextPromotion.isActive = Boolean(nextPromotion.discount && !nextPromotion?.doNotApplyPromo);
        promotion.value = nextPromotion;

        if (!promotion.value.isActive) return;
        if (!products?.items) return;

        // Preserving original mutative behavior
        products.selected = Number(promotion.value?.selected_licenses_count || 0);

        products.items = products.items.map((item: any) => ({
            ...item,
            pc: {
                ...item.pc,
                selected: Number(promotion.value?.selected_computers_count || 0),
            },
            cloud: {
                ...item.cloud,
                selected: Number(promotion.value?.[`default_cloud_capacity_${item.id}`] || 0),
            },
        }));
    }

    function initSettingsCTA(list: any[]) {
        const settings = (list || []).filter((item) => item?.productPageCTA?.productId);
        return settings.map((product) => getLicenses(product.productPageCTA));
    }

    return {
        promotions,
        licensesItems,
        productItems,
        internalProductId,
        promotion,
        hasEmailPromo,
        promotionList,
        generateSessionURL,
        findPromotionForEmails,
        findCleverBridgeCart,
        productCTA,
        promotionActive,
        getLicenses,
        licenseData,
        setPromo,
        initSettingsCTA,
        recursiveSearch,
    };
}
